import { useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  DensityToggle,
  explodeCSVAndGetObjectsFromLookup,
  formListValueFormatter,
  FormSearchResultAdvanced,
  useDataProvider,
  useLocalization,
  useMasterData,
  useStore,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getActiveInactiveStatus,
} from '@product-framework/index';
import getDefaultValueByControl from '@product-framework/utils/getDefaultValueByControl';
import { id, name, accountNumber, status } from './accessorFunctions';
import { listSchema, searchSchema } from '../../schemas';
import validationSchema from './validations';

function ListHeaderContent({ setTableDensity }) {
  return (
    <DensityToggle
      sx={{ display: 'none' }}
      onDensityChange={args => setTableDensity(args)}
    />
  );
}

function ServiceProviderList() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const store = useStore();
  const masterData = useMasterData();
  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(0);
  const [tableDensity, setTableDensity] = useState('comfortable');

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/service-providers`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEPROVIDERS_LIST_BREADCRUMB',
        defaultMessage: 'List Service Providers',
      }),
      icon: IconLibrary.ServiceProviders,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_NEW_SERVICEPROVIDER_STACKABLE_BUTTON',
        defaultMessage: 'Create Service Provider',
      }),
      Icon: IconLibrary.ServiceProvider,
      variant: 'outlined',
      permissionName: 'CAN_CREATE_SP',
      onClick: () => {
        navigate('/serviceproviders/create');
      },
    },
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      permissionName: 'CAN_SEARCH_SP',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, status: statusArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        status: statusArg === '' ? null : statusArg,
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('ServiceProviderList', searchState);
  };

  const preProcessSearchValues = searchedParams => {
    const formatterMap = {
      status: getActiveInactiveStatus,
      contractorComplianceStatus: value => {
        const statuses = explodeCSVAndGetObjectsFromLookup(
          value,
          masterData.contractorComplianceStatus,
        );
        return statuses.map(s => s.label).join(', ');
      },
      hasProfileWarnings: value => (value === 'true' ? 'Yes' : 'No'),
    };

    return formListValueFormatter({ data: searchedParams, formatterMap });
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceProviderList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      id,
      name,
      accountNumber,
      status,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    tableDensity,
    sortOrder: 'ASC',
    sortBy: 'name',
  };

  const searchProps = {
    customComponents: FormFields,
    IconLibrary,
    schema: searchSchema,
    storeName: listProps.storeName,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset(listProps.storeName);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    getValueByControl: getDefaultValueByControl,
  };

  const searchParamsProps = {
    storeName: listProps.storeName,
    preProcessSearchValues,
    schema: searchSchema,
    resultsFound,
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResultAdvanced {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'SERVICEPROVIDER_SEARCH_HEADING',
            defaultMessage: 'Search Service Providers',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        HeaderContent={<ListHeaderContent setTableDensity={setTableDensity} />}
        label={formatMessage({
          id: 'SERVICEPROVIDER_LIST_HEADING',
          defaultMessage: 'Service Providers',
        })}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceProviderList);
